import { useState } from "react";
import { Box, Typography } from "@mui/material";
import {
  Button,
  SelectInput,
  required,
  useListContext,
  useNotify,
  useRefresh,
  useStore,
} from "react-admin";
import CancelRoundedIcon from "@mui/icons-material/CancelRounded";
import CheckCircleOutlineRoundedIcon from "@mui/icons-material/CheckCircleOutlineRounded";

import permissions from "app/permissions";
import SecuredComponent from "server/security/SecuredComponent";
import FormModal from "server/components/Modals/FormModal";
import { employeeRequests } from "api/employeeApi";
import dataProvider from "server/dataProvider";
import { StaffRequestBulkAction } from "utils/constants";
import { PENDING_COUNT_VERSION_KEY } from "./PendingRequestsTitle";

const BulkActionButton = ({ requestAction, label, color, icon, ids, records, onDone }) => {
  const refresh = useRefresh();
  const notify = useNotify();
  const [, setPendingCountVersion] = useStore(PENDING_COUNT_VERSION_KEY, 0);
  const [statusReason, setStatusReason] = useState([]);
  const hasChoices = statusReason?.length > 0;

  // A reason is only offerable when the whole selection shares the same type and status —
  // the status-reasons endpoint is scoped by that pair.
  const types = new Set(records.map(r => r.type));
  const statuses = new Set(records.map(r => r.status));
  const uniformType = types.size === 1 ? [...types][0] : null;
  const uniformStatus = statuses.size === 1 ? [...statuses][0] : null;

  const title =
    requestAction === StaffRequestBulkAction.Confirm
      ? `Сигурни ли сте, че искате да одобрите избраните заявки (${ids.length})?`
      : `Сигурни ли сте, че искате да отхвърлите избраните заявки (${ids.length})?`;

  const handleGetStatusReason = async () => {
    if (uniformType == null || uniformStatus == null) {
      setStatusReason([]);
      return;
    }
    try {
      const { data } = await dataProvider.customRequest(
        employeeRequests.getStaffRequestStatusReasons({ type: uniformType, status: uniformStatus }),
        null,
        "GET",
      );
      setStatusReason(data || []);
    } catch (err) {
      console.error(err);
      setStatusReason([]);
    }
  };

  const handleSend = async formData => {
    const statusReasonId = formData?.statusReasonId ?? null;
    const recordsById = new Map(records.map(r => [r.id, r]));
    const payload = {
      staffRequests: ids.map(id => {
        const record = recordsById.get(id);
        return {
          staffRequestId: record?.staffRequestId ?? record?.id ?? id,
          statusReasonId,
        };
      }),
    };

    await dataProvider.customRequest(
      requestAction === StaffRequestBulkAction.Confirm
        ? employeeRequests.bulkApproveStaffRequests()
        : employeeRequests.bulkRejectStaffRequests(),
      payload,
      "PUT",
    );
    refresh();
    setPendingCountVersion(v => v + 1);
    onDone();
    notify("Заявките са обработени. Заявки, които не могат да бъдат обработени, остават в статус „Чакаща“.", {
      type: "success",
    });
  };

  return (
    <FormModal
      onConfirm={handleSend}
      onOpen={handleGetStatusReason}
      titleContent={"Финализирай заявки"}
      triggerButton={
        <Button
          size="medium"
          variant="outlined"
          color={color}
          label={label}
          disabled={ids.length === 0}
        >
          {icon}
        </Button>
      }
    >
      <Typography sx={{ my: 2 }}>{title}</Typography>

      {hasChoices && (
        <SelectInput
          source="statusReasonId"
          label="Причина"
          choices={statusReason}
          optionText="name"
          emptyText={false}
          fullWidth
          validate={required()}
        />
      )}
    </FormModal>
  );
};

const BulkRequestActions = () => {
  // onUnselectItems (not useUnselectAll()) — the context handler is bound to this list's selection.
  const { selectedIds = [], data = [], onUnselectItems } = useListContext();

  // Selection can outlive the current page, so only the rows we can actually see are resolved.
  const selectedRecords = selectedIds.map(id => data.find(r => r.id === id)).filter(Boolean);

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 2, mt: 2, mb: 2, ml: 2, flexWrap: "wrap" }}>
      <Typography variant="body2">Избрани заявки: {selectedIds.length}</Typography>
      <SecuredComponent permission={permissions.canApproveStaffRequest}>
        <BulkActionButton
          requestAction={StaffRequestBulkAction.Confirm}
          label={"Одобри избраните"}
          color={"success"}
          icon={<CheckCircleOutlineRoundedIcon />}
          ids={selectedIds}
          records={selectedRecords}
          onDone={onUnselectItems}
        />
      </SecuredComponent>
      <SecuredComponent permission={permissions.canRejectStaffRequest}>
        <BulkActionButton
          requestAction={StaffRequestBulkAction.Reject}
          label={"Откажи избраните"}
          color={"error"}
          icon={<CancelRoundedIcon />}
          ids={selectedIds}
          records={selectedRecords}
          onDone={onUnselectItems}
        />
      </SecuredComponent>
    </Box>
  );
};

export default BulkRequestActions;
